import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../Card';
import { ArrowRight, GitBranch } from 'lucide-react';
import { Badge } from '../Badge';
type Step = {
  to: string;
  icon: any;
  label: string;
  desc: string;
  badge?: string;
};
interface RelatedWorkflowProps {
  /** Current step label, e.g. "Deteksi" — to show position in pipeline */
  currentStep: string;
  /** Steps the operator should take next */
  nextSteps: Step[];
  /** Optional: context passed via navigation state (e.g. selected issue id) */
  context?: any;
  /** Optional: title override */
  title?: string;
}
/**
 * Reusable workflow footer that connects a detection/analysis page to its
 * downstream operator actions. Renders 3-5 next-step cards with navigation.
 * Use this on every page that produces an artifact (issue, actor, evidence)
 * which feeds into another module.
 */
export function RelatedWorkflow({
  currentStep,
  nextSteps,
  context,
  title
}: RelatedWorkflowProps) {
  const navigate = useNavigate();
  return <Card className="bg-card border-border/50" data-id="element-882">
      <div className="px-5 py-3 border-b border-border/50 bg-muted/10 flex items-center justify-between flex-wrap gap-2" data-id="element-883">
        <div className="flex items-center gap-2" data-id="element-884">
          <GitBranch className="size-4 text-primary" data-id="element-885" />
          <h3 className="font-mono text-sm uppercase tracking-wider text-foreground" data-id="element-886">
            {title ?? 'Workflow Lanjutan'}
          </h3>
        </div>
        <div className="flex items-center gap-2 font-mono text-[10px]" data-id="element-887">
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 h-5" data-id="element-888">
            ANDA DI SINI
          </Badge>
          <span className="text-foreground font-bold" data-id="element-889">{currentStep}</span>
          <ArrowRight className="size-3 text-muted-foreground" data-id="element-890" />
          <span className="text-muted-foreground" data-id="element-891">
            Pilih langkah berikutnya
          </span>
        </div>
      </div>
      <CardContent className="p-4" data-id="element-892">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-893">
          {nextSteps.map(s => {
          const Icon = s.icon;
          return <button key={s.to} onClick={() => navigate(s.to, context ? {
            state: context
          } : undefined)} className="group text-left p-3 rounded-lg border border-border/50 bg-muted/5 hover:bg-primary/5 hover:border-primary/40 transition-all" data-id="element-894">
                <div className="flex items-start justify-between mb-2" data-id="element-895">
                  <div className="size-8 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center" data-id="element-896">
                    <Icon className="size-4 text-primary" data-id="element-897" />
                  </div>
                  {s.badge && <Badge variant="outline" className="font-mono text-[8px] h-4 px-1 bg-amber-500/10 text-amber-600 border-amber-500/30" data-id="element-898">
                      {s.badge}
                    </Badge>}
                </div>
                <p className="text-sm font-bold text-foreground mb-0.5 flex items-center gap-1.5" data-id="element-899">
                  {s.label}
                  <ArrowRight className="size-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" data-id="element-900" />
                </p>
                <p className="text-[10px] text-muted-foreground leading-snug" data-id="element-901">
                  {s.desc}
                </p>
              </button>;
        })}
        </div>
      </CardContent>
    </Card>;
}